import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import AIHelpAssistant from '../../components/ui/AIHelpAssistant';
import AnnouncementsPanel from './components/AnnouncementsPanel';
import CommunityFeed from './components/CommunityFeed';
import DirectMessaging from './components/DirectMessaging';
import CreatePostModal from './components/CreatePostModal';
import { communicationService } from '../../lib/communicationService';
import { messagingService } from '../../lib/services';

const MemberCommunicationHub = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('community');
  const [announcements, setAnnouncements] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [threads, setThreads] = useState([]);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);

  useEffect(() => {
    loadInitialData();
    setupRealtimeSubscriptions();

    return () => {
      subscriptions?.forEach(sub => sub?.unsubscribe());
    };
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    await Promise.all([
      loadAnnouncements(),
      loadCommunityPosts(),
      loadThreads(),
      loadUnreadCounts()
    ]);
    setLoading(false);
  };

  const loadAnnouncements = async () => {
    const { data } = await communicationService?.getAnnouncements(userProfile?.gym_id);
    if (data) {
      setAnnouncements(data);
    }
  };

  const loadCommunityPosts = async () => {
    const { data } = await communicationService?.getCommunityPosts(userProfile?.gym_id);
    if (data) {
      setCommunityPosts(data);
    }
  };

  const loadThreads = async () => {
    const { data } = await messagingService?.getUserThreads();
    if (data) {
      setThreads(data);
    }
  };

  const loadUnreadCounts = async () => {
    const { data: announcementsCount } = await communicationService?.getUnreadAnnouncementsCount(userProfile?.gym_id);
    const { data: messagesCount } = await messagingService?.getUnreadCount();
    
    if (announcementsCount !== null) setUnreadAnnouncementsCount(announcementsCount);
    if (messagesCount !== null) setUnreadMessagesCount(messagesCount);
  };

  const setupRealtimeSubscriptions = () => {
    const postsSub = communicationService?.subscribeToCommunityPosts(
      userProfile?.gym_id,
      () => loadCommunityPosts()
    );

    const announcementsSub = communicationService?.subscribeToAnnouncements(
      userProfile?.gym_id,
      () => {
        loadAnnouncements();
        loadUnreadCounts();
      }
    );

    const messagesSub = messagingService?.subscribeToUserThreads(() => {
      loadThreads();
      loadUnreadCounts();
    });

    setSubscriptions([postsSub, announcementsSub, messagesSub]);
  };

  const handleCreatePost = async (postData) => {
    const { data, error } = await communicationService?.createCommunityPost(
      userProfile?.gym_id,
      postData
    );

    if (!error && data) {
      setIsCreatePostOpen(false);
      loadCommunityPosts();
    }

    return { data, error };
  };

  const handleMarkAnnouncementRead = async (announcementId) => {
    await communicationService?.markAnnouncementAsRead(announcementId);
    loadUnreadCounts();
  };

  const tabs = [
    { id: 'community', label: 'Community', icon: 'Users', count: null },
    { id: 'announcements', label: 'Announcements', icon: 'Megaphone', count: unreadAnnouncementsCount },
    { id: 'messages', label: 'Messages', icon: 'MessageSquare', count: unreadMessagesCount }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Communication Hub
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Stay connected with your gym community, trainers, and staff
              </p>
            </div>
            {activeTab === 'community' && (
              <Button
                onClick={() => setIsCreatePostOpen(true)}
                iconName="Plus"
                iconPosition="left"
              >
                New Post
              </Button>
            )}
          </div>

          {/* Tabs */}
          <div className="bg-card rounded-xl shadow-md border border-border mb-6">
            <div className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex-1 min-w-[150px] px-6 py-4 flex items-center justify-center gap-2 font-medium transition-colors relative
                    ${activeTab === tab?.id
                      ? 'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={20} />
                  <span>{tab?.label}</span>
                  {tab?.count > 0 && (
                    <span className="ml-1 px-2 py-0.5 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            {loading ? (
              <div className="bg-card rounded-xl p-12 shadow-md border border-border flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Loader2" size={40} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading...</p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'community' && (
                  <CommunityFeed
                    posts={communityPosts}
                    currentUserId={userProfile?.id}
                    onRefresh={loadCommunityPosts}
                  />
                )}

                {activeTab === 'announcements' && (
                  <AnnouncementsPanel
                    announcements={announcements}
                    onMarkAsRead={handleMarkAnnouncementRead}
                  />
                )}

                {activeTab === 'messages' && (
                  <DirectMessaging
                    threads={threads}
                    currentUserId={userProfile?.id}
                    onRefresh={loadThreads}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Create Post Modal */}
      {isCreatePostOpen && (
        <CreatePostModal
          onClose={() => setIsCreatePostOpen(false)}
          onCreatePost={handleCreatePost}
        />
      )}

      {/* AI Help Assistant */}
      <AIHelpAssistant
        pageContext="Member Communication Hub"
        pageName="Communication Hub"
      />
    </div>
  );
};

export default MemberCommunicationHub;